import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TimeEntry } from "@/types/time-entry";
import { useToast } from "@/components/ui/use-toast";
import { EditEntryDialog } from "./time-tracking/EditEntryDialog";
import { TimeEntryItem } from "./time-tracking/TimeEntryItem";
import { TimeEntryFilter } from "./time-tracking/TimeEntryFilter";
import { useAvailableMonths } from "@/hooks/useAvailableMonths";
import { getFilterDates } from "@/utils/dateFilters";
import { useAuth } from "@/contexts/AuthContext";

export function TimeEntryList() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>("current");
  const { user } = useAuth();
  const availableMonths = useAvailableMonths();

  const { data: timeEntries, isLoading } = useQuery({
    queryKey: ["time-entries", selectedMonth],
    queryFn: async () => {
      if (!user) throw new Error("No user found");
      
      let query = supabase
        .from("time_entries")
        .select("*")
        .eq("user_id", user.id)
        .is("deleted_at", null);

      const { startDate, endDate } = getFilterDates(selectedMonth);
      query = query
        .gte("start_time", startDate)
        .lte("start_time", endDate);
      
      const { data, error } = await query.order("start_time", { ascending: false });

      if (error) throw error;
      return data as TimeEntry[];
    },
    enabled: !!user,
  });

  const handleDelete = async (entryId: string) => {
    try {
      const { error } = await supabase
        .from("time_entries")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", entryId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["time-entries"] });
      queryClient.invalidateQueries({ queryKey: ["available-months"] });
      toast({
        title: "Eintrag gelöscht",
        description: "Der Zeiterfassungseintrag wurde erfolgreich gelöscht",
      });
    } catch {
      toast({
        title: "Fehler",
        description: "Fehler beim Löschen des Zeiterfassungseintrags",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-12 rounded bg-gray-100" />
        ))}
      </div>
    );
  }

  const totalDuration = timeEntries?.reduce((acc, entry) => {
    return acc + (entry.duration || 0);
  }, 0) || 0;

  return (
    <div className="space-y-4">
      <TimeEntryFilter
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        availableMonths={availableMonths}
        totalDuration={totalDuration}
      />

      {!timeEntries?.length ? (
        <div className="text-center py-4 text-gray-500">
          Keine Zeiteinträge im ausgewählten Zeitraum gefunden
        </div>
      ) : (
        <div className="space-y-4 animate-in fade-in-0 duration-200">
          {timeEntries.map((entry) => (
            <TimeEntryItem
              key={entry.id}
              entry={entry}
              onDelete={handleDelete}
              onEdit={setEditingEntry}
            />
          ))}
        </div>
      )}

      {editingEntry && (
        <EditEntryDialog
          entry={editingEntry}
          open={!!editingEntry}
          onOpenChange={(open) => !open && setEditingEntry(null)}
        />
      )}
    </div>
  );
}
